<script setup lang="ts">
/**
 * Barra de FICHAJE de turno, global (vive en App.vue, arriba de todo).
 *
 * Antes el turno se abría solo y en silencio, y sólo para rol 'operario' — así que a
 * Gastón y Daniel (supervisores que pickean) nunca se les abría, y su tiempo muerto no
 * tenía contra qué medirse. Ahora el fichaje es explícito y visible para cualquiera que
 * pickee o embale: entra a la mañana → "Fichar entrada"; se va → "Fichar salida".
 *
 * El tiempo fichado es el denominador del tiempo muerto en el panel de eficiencia: sin
 * fichar, no hay forma honesta de saber cuánto del día quedó sin imputar a un pedido.
 */
import { computed, ref } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useShiftStore } from '@/stores/shift';

const auth = useAuthStore();
const shift = useShiftStore();

// Sólo la gente que trabaja el piso ficha turno (los que aprueban nada más, no).
const trabajaPiso = computed(
  () => auth.employee?.can_pick !== false || auth.employee?.can_pack !== false,
);
const visible = computed(() => auth.isAuthenticated && trabajaPiso.value);

const confirmandoSalida = ref(false);
const error = ref('');

/** Hora de inicio "08:15" para mostrar desde cuándo está fichado. */
const desde = computed(() => {
  if (!shift.current) return '';
  const d = new Date(shift.current.started_at);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
});

/** Traduce el error crudo de axios (inglés) a algo accionable en español. */
function mensajeError(e: any, fallback: string): string {
  const sinRed = e?.status === 0 || ['ERR_NETWORK', 'ECONNABORTED', 'NETWORK_ERROR'].includes(e?.code);
  if (sinRed) return 'Sin señal para fichar. Probá de nuevo cuando vuelva la conexión.';
  return e?.message ?? fallback;
}

async function ficharEntrada() {
  error.value = '';
  try {
    await shift.open(auth.employee?.default_warehouse_id ?? undefined);
  } catch (e: any) {
    error.value = mensajeError(e, 'No se pudo fichar la entrada');
  }
}

async function confirmarSalida() {
  error.value = '';
  try {
    const cerro = await shift.close();
    if (cerro) confirmandoSalida.value = false;
    // Si no cerró, shift.blockingOrders tiene los pedidos que lo impiden (se muestran abajo).
  } catch (e: any) {
    error.value = mensajeError(e, 'No se pudo fichar la salida');
  }
}
</script>

<template>
  <div v-if="visible">
    <!-- SIN fichar: barra ámbar, llama a fichar -->
    <div
      v-if="!shift.isOpen"
      class="flex items-center justify-between gap-2 px-4 py-2 bg-amber-50 border-b border-amber-200"
    >
      <span class="text-sm text-amber-900">🕐 No fichaste tu entrada todavía.</span>
      <button
        class="shrink-0 text-sm font-semibold px-3 py-1.5 rounded-lg bg-amber-500 text-white active:bg-amber-600 disabled:opacity-50"
        :disabled="shift.loading"
        @click="ficharEntrada"
      >
        {{ shift.loading ? 'Fichando…' : 'Fichar entrada' }}
      </button>
    </div>

    <!-- Fichado: barra verde fina con el reloj del turno -->
    <div
      v-else
      class="flex items-center justify-between gap-2 px-4 py-1.5 bg-green-50 border-b border-green-100"
    >
      <span class="text-xs text-green-800">
        🟢 Fichado {{ desde }} · <span class="font-mono font-semibold tabular-nums">{{ shift.elapsedLabel }}</span>
      </span>
      <button
        class="shrink-0 text-xs font-medium text-gray-500 hover:text-gray-700"
        @click="confirmandoSalida = true"
      >
        Fichar salida
      </button>
    </div>

    <p v-if="error" class="text-xs text-red-700 bg-red-50 px-4 py-1">{{ error }}</p>

    <!-- Modal de fichar salida -->
    <div
      v-if="confirmandoSalida"
      class="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-4"
      @click.self="confirmandoSalida = false"
    >
      <div class="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl">
        <h2 class="font-bold text-gray-900 text-lg">¿Fichar salida?</h2>
        <p class="text-sm text-gray-600 mt-1">
          Llevás <span class="font-semibold">{{ shift.elapsedLabel }}</span> en el turno.
        </p>

        <!-- Válvula: no se puede cerrar con pedidos a medio armar -->
        <div
          v-if="shift.blockingOrders.length"
          class="mt-3 rounded-xl border border-red-300 bg-red-50 p-3"
        >
          <p class="text-sm font-semibold text-red-800">
            No podés fichar salida: tenés {{ shift.blockingOrders.length }} pedido(s) sin terminar.
          </p>
          <ul class="mt-1.5 space-y-0.5">
            <li v-for="o in shift.blockingOrders" :key="o.id" class="text-xs text-red-900">
              {{ o.tranid }} — {{ o.customer_name ?? '—' }}
            </li>
          </ul>
          <p class="text-xs text-red-700 mt-1.5">
            Terminalos, o pedile al supervisor que los libere o transfiera.
          </p>
        </div>

        <div class="mt-4 flex gap-2">
          <button
            class="flex-1 py-2.5 rounded-xl bg-gray-100 text-gray-700 font-semibold"
            @click="confirmandoSalida = false"
          >
            Volver
          </button>
          <button
            v-if="!shift.blockingOrders.length"
            class="flex-1 py-2.5 rounded-xl bg-red-500 text-white font-semibold active:bg-red-600 disabled:opacity-50"
            :disabled="shift.loading"
            @click="confirmarSalida"
          >
            {{ shift.loading ? 'Cerrando…' : 'Sí, fichar salida' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
